import { useState, useEffect, Suspense } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: () => ({
    meta: [
      { title: "Email Preferences — RKD Tile" },
      { name: "description", content: "Manage your RKD Tile email preferences and unsubscribe from notification emails." },
      { property: "og:title", content: "Email Preferences — RKD Tile" },
      { property: "og:description", content: "Manage your RKD Tile email preferences and unsubscribe from notification emails." },
      { property: "og:url", content: "https://rkdtile.com/unsubscribe" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://rkdtile.com/unsubscribe" }],
  }),
});

function UnsubscribePage() {
  return (
    <Suspense fallback={<UnsubscribeSkeleton />}>
      <UnsubscribeForm />
    </Suspense>
  );
}

function UnsubscribeSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 shadow-sm">
        <div className="h-6 w-3/4 animate-pulse rounded bg-muted" />
        <div className="mt-4 h-4 w-full animate-pulse rounded bg-muted" />
        <div className="mt-2 h-4 w-5/6 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

function UnsubscribeForm() {
  const { token } = useSearch({ from: "/unsubscribe" });
  const [status, setStatus] = useState<"loading" | "valid" | "invalid" | "confirmed" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setMessage("No unsubscribe token was provided.");
      return;
    }

    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json();
        if (res.ok && data.valid) {
          setStatus("valid");
        } else if (data.reason === "already_unsubscribed") {
          setStatus("confirmed");
          setMessage("This email address has already been unsubscribed.");
        } else {
          setStatus("invalid");
          setMessage(data.error || "This unsubscribe link is invalid or has expired.");
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Something went wrong. Please try again later.");
      });
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;

    setStatus("loading");
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setStatus("confirmed");
        setMessage("You have been successfully unsubscribed.");
      } else if (data.reason === "already_unsubscribed") {
        setStatus("confirmed");
        setMessage("This email address has already been unsubscribed.");
      } else {
        setStatus("error");
        setMessage(data.error || "We could not process your request.");
      }
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again later.");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 shadow-sm text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Unsubscribe</h1>

        {status === "loading" && (
          <p className="mt-4 text-muted-foreground">Please wait while we verify your request...</p>
        )}

        {status === "valid" && (
          <>
            <p className="mt-4 text-muted-foreground">
              Click below to confirm you no longer want to receive emails from RKD Tile.
            </p>
            <button
              onClick={handleConfirm}
              className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Confirm Unsubscribe
            </button>
          </>
        )}

        {(status === "invalid" || status === "error") && (
          <p className="mt-4 text-muted-foreground">{message}</p>
        )}

        {status === "confirmed" && (
          <>
            <p className="mt-4 text-muted-foreground">{message}</p>
            <a
              href="/"
              className="mt-6 inline-flex items-center justify-center rounded-md border border-input bg-background px-6 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              Return home
            </a>
          </>
        )}
      </div>
    </div>
  );
}
